import { createGroundElevation } from './groundElevation';
import type { Landmark, Layout } from '../data/types';
import type { Vec2 } from '../geo/types';
import { dominantAxis, planBounds, planFrameOf } from '../world/fabric/landmarks/planFrame';
import { foundationHeight } from '../world/fabric/foundation';

/** Painting-based reconstruction heights, not surveyed levels. */
export const WATER_GATE_DECK = {
  height: 2.3,
  approach: 9,
  halfWidth: 3,
  flatHalfWidth: 2,
} as const;

export function waterGateDeckProfile(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const bounds = planBounds(plan);
  const height =
    typeof landmark.params['deckHeight'] === 'number'
      ? landmark.params['deckHeight']
      : WATER_GATE_DECK.height;
  const approach =
    typeof landmark.params['approach'] === 'number'
      ? landmark.params['approach']
      : WATER_GATE_DECK.approach;
  return { plan, ...bounds, height, approach };
}

export function createWaterGateDeck(layout: Layout): (p: Vec2) => number | null {
  const elevation = createGroundElevation(layout);
  const profiles = layout.landmarks
    .filter((l) => l.kind === 'waterGate')
    .map((l) => ({ ...waterGateDeckProfile(l), base: foundationHeight(l.footprint, elevation) }));
  return (p) => {
    for (const d of profiles) {
      const dx = p[0] - d.plan.origin[0],
        dy = p[1] - d.plan.origin[1];
      const u = dx * Math.cos(d.plan.heading) + dy * Math.sin(d.plan.heading);
      const v = Math.abs(-dx * Math.sin(d.plan.heading) + dy * Math.cos(d.plan.heading));
      if (u < d.u0 - d.approach || u > d.u1 + d.approach || v > WATER_GATE_DECK.halfWidth) continue;
      const along = Math.max(
        0,
        Math.min(1, (u - d.u0 + d.approach) / d.approach, (d.u1 + d.approach - u) / d.approach)
      );
      const across = Math.min(
        1,
        (WATER_GATE_DECK.halfWidth - v) /
          (WATER_GATE_DECK.halfWidth - WATER_GATE_DECK.flatHalfWidth)
      );
      return (d.height + d.base - elevation(p)) * along * across;
    }
    return null;
  };
}
